import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Divider, Stack, Typography } from '@mui/material';
import { LinkedinTypes, ProductGroups } from 'enums';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { LinkedinAccount } from '../linkedin-accounts.types';
import StepActions from './step-actions';
import StepLayout from './step-layout';

interface Props {
  product: ProductGroups;
  isStarterExperience: boolean;
  linkedinAccount: LinkedinAccount | null;

  linkedinAccountFirstName: string | undefined;
  linkedinAccountEmail: string | undefined;

  moveToCampaignBuilder?(): void;

  handleOpenEditLinkedinAccountNameDialog?(account: LinkedinAccount): () => void;

  handleClose(): void;
}

/**
 * Der Abschluss trägt keine Schrittzählung mehr — er zählt auf, was jetzt gilt.
 *
 * Die drei Zeilen sind genau die Punkte, an denen der App-Login sich vom alten
 * Browser-Login unterscheidet: eigene feste IP, vertrautes Gerät, aktives Produkt. Wer
 * das liest, weiß, warum ab jetzt kein erneutes Anmelden mehr kommt.
 */
const SyncSuccessful: React.FC<Props> = ({
  isStarterExperience,
  linkedinAccount,
  linkedinAccountFirstName,
  linkedinAccountEmail,
  moveToCampaignBuilder,
  handleOpenEditLinkedinAccountNameDialog,
  handleClose,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  useEffect(() => {
    if (_.isNil(linkedinAccount)) return;
    if (!linkedinAccount.firstName || !linkedinAccount.lastName) {
      handleOpenEditLinkedinAccountNameDialog?.(linkedinAccount)();
    }
  }, [linkedinAccount]);

  const liProductType = linkedinAccount?.liProductType || LinkedinTypes.LINKEDIN;
  const productLabel =
    liProductType === LinkedinTypes.RECRUITER
      ? t('appSyncProductRecruiter', 'Recruiter')
      : liProductType === LinkedinTypes.SALES_NAV
      ? t('appSyncProductSalesNavigator', 'Sales Navigator')
      : t('appSyncProductLinkedin', 'LinkedIn Standard');

  const accountLabel = linkedinAccountFirstName || linkedinAccountEmail || '';

  const facts = [
    t('appSyncDoneFactIp', 'Feste IP & Gerät zugewiesen'),
    t('appSyncDoneFactTrust', 'Gerät vertraut — künftige Logins ohne 2FA'),
    t('appSyncDoneFactProduct', '{{product}} aktiv', { product: productLabel }),
  ];

  return (
    <StepLayout
      align={'center'}
      title={t('appSyncDoneTitle', 'Konto verbunden')}
      description={t('appSyncDoneDescription', '{{name}} ist startklar. Ab jetzt läuft alles automatisch — kein erneutes Anmelden nötig.', {
        name: accountLabel,
      })}
      footer={
        isStarterExperience && !_.isNil(moveToCampaignBuilder) ? (
          <StepActions primaryText={t('next')} onPrimary={moveToCampaignBuilder} />
        ) : (
          <StepActions primaryText={t('appSyncGoToAccounts', 'Zu meinen Konten')} onPrimary={handleClose} />
        )
      }
    >
      <Stack alignItems={'center'} mb={'22px'}>
        <div className={classes.badgeOuter}>
          <CheckCircleIcon className={classes.badgeIcon} />
        </div>
      </Stack>
      <Stack className={classes.factList}>
        {facts.map((fact, index) => (
          <React.Fragment key={fact}>
            {index !== 0 ? <Divider /> : null}
            <Stack direction={'row'} alignItems={'center'} gap={1.5} className={classes.factRow}>
              <CheckIcon className={classes.factIcon} />
              <Typography className={classes.factText}>{fact}</Typography>
            </Stack>
          </React.Fragment>
        ))}
      </Stack>
    </StepLayout>
  );
};

const useStyles = makeStyles()((theme) => ({
  badgeOuter: {
    width: '76px',
    height: '76px',
    borderRadius: '50%',
    background: theme.palette.backgrounds.lightGreen,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeIcon: {
    fontSize: theme.typography.pxToRem(52),
    color: theme.palette.success.main,
  },
  factList: {
    border: `1px solid ${theme.palette.greys.lighter}`,
    borderRadius: '12px',
  },
  factRow: {
    padding: '12px 14px',
  },
  factIcon: {
    fontSize: theme.typography.pxToRem(18),
    color: theme.palette.success.main,
  },
  factText: {
    fontSize: theme.typography.pxToRem(14),
  },
}));

export default SyncSuccessful;
